import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import * as globals from '../../components/GlobalScripts.js';

import '../../styles/admin/menu_semanal.css';

function getPathApi() {
    return String(localStorage.getItem('path_api') || '').replace(/\/$/, '');
}

function safeJsonParse(maybeJson, fallback = null) {
    try {
        if (typeof maybeJson === 'string') return JSON.parse(maybeJson);
        if (maybeJson && typeof maybeJson === 'object') return maybeJson;
    } catch { }
    return fallback;
}

function uid(prefix = 'id') {
    return `${prefix}_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

function toDateObj(yyyyMMdd) {
    if (!yyyyMMdd) return null;
    const d = new Date(`${yyyyMMdd}T00:00:00`);
    return Number.isNaN(d.getTime()) ? null : d;
}

function toYMD(dateObj) {
    if (!(dateObj instanceof Date)) return '';
    const y = dateObj.getFullYear();
    const m = String(dateObj.getMonth() + 1).padStart(2, '0');
    const d = String(dateObj.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}

function addDays(dateObj, days) {
    const d = new Date(dateObj.getTime());
    d.setDate(d.getDate() + days);
    return d;
}

function normalizeToMonday(ymd) {
    const d = toDateObj(ymd);
    if (!d) return '';
    const dow = d.getDay();
    const diff = (dow + 6) % 7;
    return toYMD(addDays(d, -diff));
}

function makeEmptyMenu() {
    const today = new Date();
    const start = normalizeToMonday(toYMD(today)) || '';
    const startObj = toDateObj(start);
    const end = startObj ? toYMD(addDays(startObj, 4)) : '';

    return {
        week: {
            start_date: start,
            end_date: end,
            active_days: ['mon', 'tue', 'wed', 'thu', 'fri'],
        },
        days: {
            mon: { options: [] },
            tue: { options: [] },
            wed: { options: [] },
            thu: { options: [] },
            fri: { options: [] },
        },
        meta: {
            draft_updated_at: null,
            published_at: null,
        },
    };
}

const DAY_META = [
    { key: 'mon', label: 'Lunes', offset: 0 },
    { key: 'tue', label: 'Martes', offset: 1 },
    { key: 'wed', label: 'Miércoles', offset: 2 },
    { key: 'thu', label: 'Jueves', offset: 3 },
    { key: 'fri', label: 'Viernes', offset: 4 },
];

const SCOPE_META = [
    { value: 'both', label: 'Almuerzo y cena' },
    { value: 'lunch', label: 'Solo almuerzo' },
    { value: 'dinner', label: 'Solo cena' },
];

function scopeLabel(scope) {
    const found = SCOPE_META.find((s) => s.value === scope);
    return found ? found.label : 'Almuerzo y cena';
}

function scopeBadge(scope) {
    if (scope === 'lunch') return 'Almuerzo';
    if (scope === 'dinner') return 'Cena';
    return 'Ambas';
}

function appliesTo(scope, kind) {
    if (scope === 'both') return true;
    return scope === kind;
}

function buildAuthPayload() {
    return {
        rute: globals.getRuteBackend(),
        session_id: globals.get_session_id(),
        keylogin: globals.get_keylogin(),
    };
}

function notify(type, title, msg) {
    try {
        globals.createAlert({
            type,
            msg_title: title,
            msg,
            effect: 'opacidad 2s, desplazamiento 2s',
            time_life: 3500,
        });
    } catch { }
}

function mergeById(prevArr = [], nextArr = []) {
    const m = new Map();
    [...(prevArr || []), ...(nextArr || [])].forEach((it) => {
        const id = Number(it?.id || 0);
        if (!id) return;
        m.set(id, it);
    });
    return Array.from(m.values());
}

function collectIdsFromMenu(menu) {
    const out = { fuerte: new Set(), entrada: new Set(), snack: new Set() };
    if (!menu || !menu.days) return out;

    DAY_META.forEach((d) => {
        const opts = Array.isArray(menu?.days?.[d.key]?.options) ? menu.days[d.key].options : [];
        opts.forEach((o) => {
            const mid = Number(o?.main_id || 0);
            if (mid) out.fuerte.add(mid);

            const entryIds = Array.isArray(o?.entry_ids) ? o.entry_ids : [];
            entryIds.forEach((id) => {
                const n = Number(id || 0);
                if (n) out.entrada.add(n);
            });

            const isDinnerOnly = (o?.scope || 'both') === 'dinner';
            const snackIds = isDinnerOnly ? [] : (Array.isArray(o?.snack_ids) ? o.snack_ids : []);
            snackIds.forEach((id) => {
                const n = Number(id || 0);
                if (n) out.snack.add(n);
            });
        });
    });

    return out;
}

function SvgIcon({ name }) {
    const common = {
        className: 'tw-menuweek-ico',
        viewBox: '0 0 24 24',
        fill: 'none',
        xmlns: 'http://www.w3.org/2000/svg',
    };

    if (name === 'plus') {
        return (
            <svg {...common}>
                <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
        );
    }
    if (name === 'x') {
        return (
            <svg {...common}>
                <path d="M18 6 6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
        );
    }
    if (name === 'chev') {
        return (
            <svg {...common}>
                <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        );
    }
    if (name === 'save') {
        return (
            <svg {...common}>
                <path
                    d="M5 20h14a1 1 0 0 0 1-1V8l-3-3H6a1 1 0 0 0-1 1v13a1 1 0 0 0 1 1Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinejoin="round"
                />
                <path d="M16 20v-6H8v6" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
                <path d="M9 5v4h6V5" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
            </svg>
        );
    }
    if (name === 'undo') {
        return (
            <svg {...common}>
                <path d="M9 14 4 9l5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M20 20a8 8 0 0 0-8-8H4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
        );
    }
    if (name === 'upload') {
        return (
            <svg {...common}>
                <path d="M12 3v12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                <path d="M7 8l5-5 5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M5 21h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
        );
    }
    if (name === 'trash') {
        return (
            <svg {...common}>
                <path d="M3 6h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                <path
                    d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinejoin="round"
                />
                <path
                    d="M6 6l1 15a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-15"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinejoin="round"
                />
                <path d="M10 11v6M14 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
        );
    }
    if (name === 'edit') {
        return (
            <svg {...common}>
                <path d="M12 20h9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                <path
                    d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinejoin="round"
                />
            </svg>
        );
    }
    return null;
}

function Modal({ open, title, onClose, children, footer }) {
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, onClose]);

    if (!open) return null;

    return (
        <div className="tw-menuweek-modal__overlay" onMouseDown={onClose}>
            <div className="tw-menuweek-modal" onMouseDown={(e) => e.stopPropagation()}>
                <div className="tw-menuweek-modal__head">
                    <div className="tw-menuweek-modal__title">{title}</div>
                    <button className="tw-menuweek-iconbtn" onClick={onClose} type="button" aria-label="Cerrar">
                        <SvgIcon name="x" />
                    </button>
                </div>

                <div className="tw-menuweek-modal__body">{children}</div>

                {footer ? <div className="tw-menuweek-modal__footer">{footer}</div> : null}
            </div>
        </div>
    );
}

function PickerModal({
    open,
    title,
    items,
    loading = false,
    hasMore = false,
    mode = 'single',
    valueSingle = null,
    valueMulti = [],
    query = '',
    onQueryChange,
    onSearch,
    onClose,
    onConfirm,
    onLoadMore,
}) {
    const [single, setSingle] = useState(valueSingle);
    const [multi, setMulti] = useState(Array.isArray(valueMulti) ? valueMulti : []);

    useEffect(() => {
        if (!open) return;
        setSingle(valueSingle || null);
        setMulti(Array.isArray(valueMulti) ? valueMulti : []);
    }, [open, valueSingle, valueMulti]);

    const toggleMulti = (id) => {
        setMulti((prev) => {
            const s = new Set(prev || []);
            if (s.has(id)) s.delete(id);
            else s.add(id);
            return Array.from(s);
        });
    };

    const runSearch = () => {
        const q = String(query || '').trim();
        onSearch?.(q);
    };

    return (
        <Modal
            open={open}
            title={title}
            onClose={onClose}
            footer={
                <div className="tw-menuweek-modal__actions">
                    <button className="tw-menuweek-btn tw-menuweek-btn--ghost" onClick={onClose} type="button">
                        Cancelar
                    </button>
                    <button
                        className="tw-menuweek-btn tw-menuweek-btn--primary"
                        onClick={() => { if (mode === 'single') onConfirm?.(single); else onConfirm?.(multi); }}
                        type="button"
                    >
                        Confirmar
                    </button>
                </div>
            }
        >
            <div className="tw-menuweek-field">
                <div className="tw-menuweek-label">Buscar</div>

                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <input
                        className="tw-menuweek-input"
                        value={query}
                        onChange={(e) => onQueryChange?.(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                runSearch();
                            }
                        }}
                        placeholder="Escribe y presiona Enter…"
                    />

                    <button
                        className="tw-menuweek-btn tw-menuweek-btn--ghost"
                        type="button"
                        onClick={runSearch}
                        disabled={loading}
                        title="Buscar"
                    >
                        Buscar
                    </button>
                </div>

                {loading ? <div className="tw-menuweek-help">Buscando…</div> : <div className="tw-menuweek-help">Presiona Enter para buscar.</div>}
            </div>

            <div className="tw-menuweek-picker">
                {items?.length ? (
                    items.map((it) => {
                        const id = Number(it?.id || 0);
                        const isSelected = mode === 'single' ? single === id : (multi || []).includes(id);

                        return (
                            <button
                                key={id}
                                type="button"
                                className={`tw-menuweek-picker__row ${isSelected ? 'is-selected' : ''}`}
                                onClick={() => { if (mode === 'single') setSingle(id); else toggleMulti(id); }}
                            >
                                <div className="tw-menuweek-picker__main">
                                    <div className="tw-menuweek-picker__name">{it?.nombre || '—'}</div>
                                    {it?.descripcion ? <div className="tw-menuweek-picker__sub">{it.descripcion}</div> : null}
                                </div>

                                <div className="tw-menuweek-picker__check">
                                    <div className={`tw-menuweek-check ${isSelected ? 'is-on' : ''}`} />
                                </div>
                            </button>
                        );
                    })
                ) : (
                    <div className="tw-menuweek-empty">
                        {loading ? 'Buscando…' : 'No hay resultados con ese filtro.'}
                    </div>
                )}
            </div>

            {hasMore ? (
                <div style={{ marginTop: 12, display: 'flex', justifyContent: 'center' }}>
                    <button
                        className="tw-menuweek-btn tw-menuweek-btn--ghost"
                        type="button"
                        onClick={onLoadMore}
                        disabled={loading}
                    >
                        Cargar más
                    </button>
                </div>
            ) : null}
        </Modal>
    );
}

function OptionCard({ option, fuerteName, entradasCount, snacksCount, onEdit, onRemove }) {
    return (
        <div className="tw-menuweek-optioncard">
            <div className="tw-menuweek-optioncard__top">
                <div className="tw-menuweek-optioncard__title">
                    <div className="tw-menuweek-badge">{scopeBadge(option?.scope)}</div>
                    <div className="tw-menuweek-optioncard__name">{fuerteName || 'Selecciona un plato fuerte'}</div>
                </div>

                <div className="tw-menuweek-optioncard__actions">
                    <button className="tw-menuweek-iconbtn" type="button" onClick={onEdit} aria-label="Editar">
                        <SvgIcon name="edit" />
                    </button>
                    <button className="tw-menuweek-iconbtn tw-menuweek-iconbtn--danger" type="button" onClick={onRemove} aria-label="Eliminar">
                        <SvgIcon name="trash" />
                    </button>
                </div>
            </div>

            <div className="tw-menuweek-optioncard__meta">
                <div className="tw-menuweek-pill">Ensaladas: {entradasCount}</div>
                <div className="tw-menuweek-pill">Snacks: {snacksCount}</div>
            </div>
        </div>
    );
}

function StepPill({ n, title, active }) {
    return (
        <div className={`tw-menuweek-step ${active ? 'is-active' : ''}`}>
            <div className="tw-menuweek-step__n">{n}</div>
            <div className="tw-menuweek-step__t">{title}</div>
        </div>
    );
}

export default function Admin_menu_semanal() {
    const path_api = getPathApi();
    const busyRef = useRef(0);

    const busyWrap = async (fn) => {
        try {
            if (busyRef.current === 0) {
                globals.openViewCharge();
                await new Promise(requestAnimationFrame);
            }
            busyRef.current += 1;
            return await fn();
        } finally {
            busyRef.current = Math.max(0, busyRef.current - 1);
            if (busyRef.current === 0) globals.closeViewCharge();
        }
    };

    const PICKER_LIMIT = 50;
    const MAX_WARM_PAGES = 20;

    const [draft, setDraft] = useState(makeEmptyMenu());
    const [published, setPublished] = useState(null);
    const [dirty, setDirty] = useState(false);

    const [platillos, setPlatillos] = useState({ fuerte: [], entrada: [], snack: [] });

    const fuerteMap = useMemo(() => new Map((platillos.fuerte || []).map((p) => [Number(p.id), p])), [platillos.fuerte]);
    const entradaMap = useMemo(() => new Map((platillos.entrada || []).map((p) => [Number(p.id), p])), [platillos.entrada]);
    const snackMap = useMemo(() => new Map((platillos.snack || []).map((p) => [Number(p.id), p])), [platillos.snack]);

    const [selectedDay, setSelectedDay] = useState('mon');

    const [editing, setEditing] = useState({
        open: false,
        dayKey: null,
        optionId: null,
        isNew: false,
        prevDirty: false,
    });
    const [localOpt, setLocalOpt] = useState(null);

    const [picker, setPicker] = useState({ open: false, kind: null });
    const pickerReqRef = useRef(0);

    const [pickerData, setPickerData] = useState({
        q: '',
        page: 1,
        items: [],
        loading: false,
        hasMore: false,
    });

    const activeDayKeys = useMemo(() => {
        const arr = Array.isArray(draft?.week?.active_days) ? draft.week.active_days : [];
        return DAY_META.map((d) => d.key).filter((k) => arr.includes(k));
    }, [draft?.week?.active_days]);

    useEffect(() => {
        if (!activeDayKeys.length) return;
        if (!activeDayKeys.includes(selectedDay)) setSelectedDay(activeDayKeys[0]);
    }, [activeDayKeys, selectedDay]);

    const startDate = draft?.week?.start_date || '';
    const startObj = useMemo(() => toDateObj(startDate), [startDate]);

    const dayDateLabel = useMemo(() => {
        const map = {};
        const base = startObj;
        if (!base) return map;
        DAY_META.forEach((d) => {
            map[d.key] = toYMD(addDays(base, d.offset));
        });
        return map;
    }, [startObj]);

    const canBuild = useMemo(() => {
        const s = draft?.week?.start_date;
        const e = draft?.week?.end_date;
        if (!s || !e) return false;

        const sObj = toDateObj(s);
        const eObj = toDateObj(e);
        if (!sObj || !eObj) return false;

        if (sObj.getDay() !== 1) return false;
        if (eObj.getDay() !== 5) return false;
        if (eObj.getTime() < sObj.getTime()) return false;

        return activeDayKeys.length > 0;
    }, [draft?.week?.start_date, draft?.week?.end_date, activeDayKeys.length]);

    const updateDraft = (updater) => {
        setDraft((prev) => {
            const next = typeof updater === 'function' ? updater(prev) : updater;
            return next;
        });
        setDirty(true);
    };

    const fetchPlatillosTipo = async (tipo, { q = '', page = 1, limit = PICKER_LIMIT } = {}) => {
        const payload = {
            ...buildAuthPayload(),
            q,
            tipo,
            activo: 1,
            page,
            limit,
        };

        const res = await axios.post(`${path_api}/admin/platillos/list.php`, payload);
        if (res.data?.mensaje !== 'success') return [];

        const items = Array.isArray(res.data?.items) ? res.data.items : [];
        return items
            .map((r) => ({
                id: r.id ?? null,
                nombre: r.data?.nombre ?? '',
                descripcion: r.data?.descripcion ?? '',
                tipo: r.tipo ?? '',
                activo: r.activo ?? 1,
                macros: safeJsonParse(r.macros, null),
                alergenos: r.data?.alergenos_disclaimer ?? '',
            }))
            .filter((x) => !!x.id);
    };

    const fetchMenus = async () => {
        const payload = { ...buildAuthPayload() };
        const res = await axios.post(`${path_api}/admin/menu_week/get.php`, payload);
        if (res.data?.mensaje !== 'success') return { draft: null, published: null };
        return { draft: res.data?.draft ?? null, published: res.data?.published ?? null };
    };

    const saveDraftApi = async (menu) => {
        const payload = { ...buildAuthPayload(), menu };
        const res = await axios.post(`${path_api}/admin/menu_week/save_draft.php`, payload);
        return res.data?.mensaje === 'success' ? (res.data?.draft ?? menu) : null;
    };

    const publishApi = async (menu) => {
        const payload = { ...buildAuthPayload(), menu };
        const res = await axios.post(`${path_api}/admin/menu_week/publish.php`, payload);
        if (res.data?.mensaje !== 'success') return null;
        return { draft: res.data?.draft ?? menu, published: res.data?.published ?? menu };
    };

    const revertApi = async () => {
        const payload = { ...buildAuthPayload() };
        const res = await axios.post(`${path_api}/admin/menu_week/revert.php`, payload);
        if (res.data?.mensaje !== 'success') return null;
        return { draft: res.data?.draft ?? null, published: res.data?.published ?? null };
    };

    const resetApi = async () => {
        const payload = { ...buildAuthPayload() };
        const res = await axios.post(`${path_api}/admin/menu_week/reset.php`, payload);
        return res.data?.mensaje === 'success' ? (res.data?.draft ?? null) : null;
    };

    const warmCacheKind = async (kind, neededIdsSet) => {
        let page = 1;
        let merged = [];
        let found = new Set();

        const pushItems = (items) => {
            merged = mergeById(merged, items);
            merged.forEach((it) => found.add(Number(it.id)));
        };

        pushItems(platillos?.[kind] || []);

        const need = neededIdsSet instanceof Set ? neededIdsSet : new Set();
        const needsAny = need.size > 0;

        const mustFetchAtLeastOne = !needsAny;

        for (let i = 0; i < MAX_WARM_PAGES; i += 1) {
            const missing = new Set([...need].filter((id) => !found.has(Number(id))));
            const alreadyOk = needsAny ? (missing.size === 0) : (merged.length > 0);

            if (alreadyOk && !mustFetchAtLeastOne) break;
            if (alreadyOk && mustFetchAtLeastOne) break;

            const items = await fetchPlatillosTipo(kind, { q: '', page, limit: PICKER_LIMIT });
            if (!items.length) break;

            pushItems(items);

            if (items.length < PICKER_LIMIT) break;

            const stillMissing = new Set([...need].filter((id) => !found.has(Number(id))));
            if (needsAny && stillMissing.size === 0) break;

            page += 1;
        }

        if (!merged.length && mustFetchAtLeastOne) {
            const items = await fetchPlatillosTipo(kind, { q: '', page: 1, limit: PICKER_LIMIT });
            pushItems(items);
        }

        return merged;
    };

    const searchPicker = async ({ kind, q = '', page = 1, append = false }) => {
        const reqId = ++pickerReqRef.current;

        setPickerData((p) => ({
            ...p,
            q,
            page,
            loading: true,
        }));

        try {
            const items = await fetchPlatillosTipo(kind, { q, page, limit: PICKER_LIMIT });

            if (reqId !== pickerReqRef.current) return;

            setPlatillos((prev) => ({
                ...prev,
                [kind]: append ? mergeById(prev?.[kind] || [], items) : mergeById(prev?.[kind] || [], items),
            }));

            setPickerData((p) => ({
                ...p,
                q,
                page,
                items: append ? mergeById(p.items, items) : items,
                loading: false,
                hasMore: items.length >= PICKER_LIMIT,
            }));
        } catch (e) {
            if (reqId !== pickerReqRef.current) return;
            setPickerData((p) => ({ ...p, loading: false, items: append ? p.items : [] }));
            notify('error', 'Error', 'No se pudo buscar platillos. Intenta de nuevo.');
        }
    };

    useEffect(() => {
        let mounted = true;

        busyWrap(async () => {
            try {
                const menus = await fetchMenus();
                if (!mounted) return;

                const dra = menus?.draft || null;
                const pub = menus?.published || null;

                if (pub) setPublished(pub);

                if (dra) {
                    setDraft(dra);
                    setDirty(false);
                } else if (pub) {
                    setDraft(pub);
                    setDirty(true);
                } else {
                    setDraft(makeEmptyMenu());
                    setDirty(true);
                }

                const baseMenu = dra || pub || null;
                const needed = collectIdsFromMenu(baseMenu);

                const [fuertes, entradas, snacks] = await Promise.all([
                    warmCacheKind('fuerte', needed.fuerte),
                    warmCacheKind('entrada', needed.entrada),
                    warmCacheKind('snack', needed.snack),
                ]);

                if (!mounted) return;
                setPlatillos({
                    fuerte: fuertes || [],
                    entrada: entradas || [],
                    snack: snacks || [],
                });
            } catch {
                notify('error', 'Error', 'No se pudo cargar la información del menú semanal.');
            }
        });

        return () => { mounted = false; };
    }, []);

    const onChangeStartDate = (value) => {
        const monday = normalizeToMonday(value);
        if (!monday) return;

        const mondayObj = toDateObj(monday);
        const friday = mondayObj ? toYMD(addDays(mondayObj, 4)) : '';

        updateDraft((prev) => {
            const next = safeJsonParse(JSON.stringify(prev), makeEmptyMenu());
            next.week = next.week || {};
            next.week.start_date = monday;
            next.week.end_date = friday;
            return next;
        });

        if (monday !== value) {
            notify('success', 'Ajustado', 'La fecha de inicio se ajustó automáticamente al lunes de esa semana.');
        }
    };

    const toggleDay = (dayKey) => {
        updateDraft((prev) => {
            const next = safeJsonParse(JSON.stringify(prev), makeEmptyMenu());
            next.week = next.week || {};
            next.days = next.days || {};

            const current = Array.isArray(next.week.active_days) ? next.week.active_days : [];
            const isOn = current.includes(dayKey);

            if (isOn) {
                next.week.active_days = current.filter((d) => d !== dayKey);
                next.days[dayKey] = { options: [] };
            } else {
                next.week.active_days = Array.from(new Set([...current, dayKey]));
                next.days[dayKey] = { options: [] };
            }

            return next;
        });
    };

    const dayOptions = useMemo(() => {
        const d = draft?.days?.[selectedDay] || { options: [] };
        return Array.isArray(d.options) ? d.options : [];
    }, [draft?.days, selectedDay]);

    const usedMainIdsByKind = (dayKey, kind, ignoreOptionId = null) => {
        const opts = Array.isArray(draft?.days?.[dayKey]?.options) ? draft.days[dayKey].options : [];
        const used = new Set();
        opts.forEach((o) => {
            const mid = Number(o?.main_id || 0);
            if (!mid) return;
            if (ignoreOptionId && o.id === ignoreOptionId) return;
            if (appliesTo(o.scope || 'both', kind)) used.add(mid);
        });
        return used;
    };

    const openEditor = (dayKey, optionId, { isNew = false, prevDirty = dirty } = {}) => {
        const opts = Array.isArray(draft?.days?.[dayKey]?.options) ? draft.days[dayKey].options : [];
        const found = opts.find((o) => o.id === optionId);

        const base = found
            ? safeJsonParse(JSON.stringify(found), found)
            : { id: optionId, scope: 'both', main_id: null, entry_ids: [], snack_ids: [] };

        if ((base.scope || 'both') === 'dinner') base.snack_ids = [];

        setLocalOpt(base);
        setEditing({ open: true, dayKey, optionId, isNew, prevDirty });
    };

    const closeEditor = ({ discard = false } = {}) => {
        if (discard && editing.open && editing.isNew && editing.dayKey && editing.optionId) {
            const dayKey = editing.dayKey;
            const optionId = editing.optionId;
            const restoreDirty = !!editing.prevDirty;

            setDraft((prev) => {
                const next = safeJsonParse(JSON.stringify(prev), makeEmptyMenu());
                next.days = next.days || {};
                next.days[dayKey] = next.days[dayKey] || { options: [] };

                const arr = Array.isArray(next.days[dayKey].options) ? next.days[dayKey].options : [];
                next.days[dayKey].options = arr.filter((o) => o.id !== optionId);

                return next;
            });

            setDirty(restoreDirty);
        }

        setEditing({ open: false, dayKey: null, optionId: null, isNew: false, prevDirty: false });
        setLocalOpt(null);

        setPicker({ open: false, kind: null });
        setPickerData({ q: '', page: 1, items: [], loading: false, hasMore: false });
        pickerReqRef.current += 1;
    };

    const addOption = () => {
        const optionId = uid('opt');
        const prevDirty = dirty;

        updateDraft((prev) => {
            const next = safeJsonParse(JSON.stringify(prev), makeEmptyMenu());
            next.days = next.days || {};
            next.days[selectedDay] = next.days[selectedDay] || { options: [] };

            const arr = Array.isArray(next.days[selectedDay].options) ? next.days[selectedDay].options : [];
            arr.push({ id: optionId, scope: 'both', main_id: null, entry_ids: [], snack_ids: [] });

            next.days[selectedDay].options = arr;
            return next;
        });

        openEditor(selectedDay, optionId, { isNew: true, prevDirty });
    };

    const removeOption = (optionId) => {
        const ok = window.confirm('¿Eliminar esta opción del día?');
        if (!ok) return;

        updateDraft((prev) => {
            const next = safeJsonParse(JSON.stringify(prev), makeEmptyMenu());
            const arr = Array.isArray(next?.days?.[selectedDay]?.options) ? next.days[selectedDay].options : [];
            next.days[selectedDay].options = arr.filter((o) => o.id !== optionId);
            return next;
        });
    };

    const saveOption = () => {
        if (!editing.open || !editing.dayKey || !localOpt) return;

        const optToSave =
            (localOpt.scope || 'both') === 'dinner'
                ? { ...localOpt, snack_ids: [] }
                : localOpt;

        updateDraft((prev) => {
            const next = safeJsonParse(JSON.stringify(prev), makeEmptyMenu());
            const arr = Array.isArray(next?.days?.[editing.dayKey]?.options) ? next.days[editing.dayKey].options : [];
            next.days[editing.dayKey].options = arr.map((o) => (o.id === editing.optionId ? optToSave : o));
            return next;
        });

        closeEditor({ discard: false });
    };

    const scopeConflict = useMemo(() => {
        if (!editing.open || !localOpt?.main_id) return null;

        const s = localOpt.scope || 'both';
        const dayKey = editing.dayKey;

        const usedLunch = usedMainIdsByKind(dayKey, 'lunch', localOpt.id);
        const usedDinner = usedMainIdsByKind(dayKey, 'dinner', localOpt.id);

        const mid = Number(localOpt.main_id || 0);
        if (!mid) return null;

        if (s === 'both') {
            if (usedLunch.has(mid) || usedDinner.has(mid)) return 'Ya existe este plato fuerte en almuerzo o cena para este día.';
        } else if (s === 'lunch') {
            if (usedLunch.has(mid)) return 'Ya existe este plato fuerte en almuerzo para este día.';
        } else if (s === 'dinner') {
            if (usedDinner.has(mid)) return 'Ya existe este plato fuerte en cena para este día.';
        }
        return null;
    }, [editing.open, editing.dayKey, localOpt, draft]);

    const saveDraftClick = async () => {
        if (!canBuild) {
            notify('error', 'Falta información', 'Revisa que la semana sea de lunes a viernes y que haya al menos un día con venta.');
            return;
        }

        await busyWrap(async () => {
            const saved = await saveDraftApi(draft);
            if (saved) {
                setDraft(saved);
                setDirty(false);
                notify('success', 'Guardado', 'Borrador guardado correctamente.');
            } else {
                notify('error', 'Error', 'No se pudo guardar el borrador.');
            }
        });
    };

    const publishClick = async () => {
        if (!canBuild) {
            notify('error', 'Falta información', 'Completa la configuración antes de publicar.');
            return;
        }
        const okConfirm = window.confirm('¿Publicar el menú semanal? Esto reemplazará el menú publicado.');
        if (!okConfirm) return;

        await busyWrap(async () => {
            const out = await publishApi(draft);
            if (!out) {
                notify('error', 'Error', 'No se pudo publicar el menú.');
                return;
            }
            setDraft(out.draft || draft);
            setPublished(out.published || out.draft || draft);
            setDirty(false);
            notify('success', 'Publicado', 'Menú semanal publicado correctamente.');
        });
    };

    const revertClick = async () => {
        const okConfirm = window.confirm('¿Revertir el borrador al menú publicado? Se perderán cambios del borrador.');
        if (!okConfirm) return;

        await busyWrap(async () => {
            const out = await revertApi();
            if (!out?.draft) {
                notify('error', 'Error', 'No se pudo revertir el borrador.');
                return;
            }
            setDraft(out.draft);
            if (out.published) setPublished(out.published);
            setDirty(false);
            notify('success', 'Revertido', 'El borrador fue reemplazado por la versión publicada.');
        });
    };

    const startNewClick = async () => {
        const okConfirm = window.confirm('¿Empezar un menú nuevo? Se borrará el borrador actual.');
        if (!okConfirm) return;

        await busyWrap(async () => {
            const dra = await resetApi();
            const empty = dra || makeEmptyMenu();
            setDraft(empty);
            setDirty(true);
            notify('success', 'Listo', 'Borrador limpio. Ya puedes armar un menú nuevo.');
        });
    };

    const selectedDayMeta = DAY_META.find((d) => d.key === selectedDay);
    const selectedDayLabel = selectedDayMeta ? selectedDayMeta.label : 'Día';

    const dayCounters = useMemo(() => {
        const out = {};
        DAY_META.forEach((d) => {
            const opts = Array.isArray(draft?.days?.[d.key]?.options) ? draft.days[d.key].options : [];
            const lunch = opts.filter((o) => appliesTo(o.scope || 'both', 'lunch')).length;
            const dinner = opts.filter((o) => appliesTo(o.scope || 'both', 'dinner')).length;
            out[d.key] = { lunch, dinner, total: opts.length };
        });
        return out;
    }, [draft?.days]);

    const openPicker = (kind) => {
        if (kind === 'snack' && (localOpt?.scope || 'both') === 'dinner') return;

        setPicker({ open: true, kind });

        pickerReqRef.current += 1;
        setPickerData({ q: '', page: 1, items: [], loading: false, hasMore: false });

        searchPicker({ kind, q: '', page: 1, append: false });
    };

    const closePicker = () => {
        setPicker({ open: false, kind: null });
        setPickerData({ q: '', page: 1, items: [], loading: false, hasMore: false });
        pickerReqRef.current += 1;
    };

    const onPickerConfirm = (val) => {
        if (!localOpt) return;

        if (picker.kind === 'fuerte') {
            setLocalOpt((p) => ({ ...p, main_id: val || null }));
        } else if (picker.kind === 'entrada') {
            setLocalOpt((p) => ({ ...p, entry_ids: Array.isArray(val) ? val : [] }));
        } else if (picker.kind === 'snack') {
            setLocalOpt((p) => ({ ...p, snack_ids: Array.isArray(val) ? val : [] }));
        }

        closePicker();
    };

    const publishedLabel = useMemo(() => {
        const s = published?.week?.start_date || '';
        const e = published?.week?.end_date || '';
        if (!s || !e) return '—';
        return `${s} → ${e}`;
    }, [published]);

    useEffect(() => {
        if ((localOpt?.scope || 'both') === 'dinner' && picker.kind === 'snack') {
            closePicker();
        }
    }, [localOpt?.scope, picker.kind]);

    const isDinnerOnly = (localOpt?.scope || 'both') === 'dinner';

    return (
        <div className="tw-menuweek-page container-section flex-column gap-20">
            <div className="tw-menuweek-topbar">
                <div className="tw-menuweek-topbar__left">
                    <div className="tw-menuweek-title">Menú semanal</div>
                    <div className="tw-menuweek-subtitle">
                        Publicado: <span className="tw-menuweek-subtitle__strong">{publishedLabel}</span>
                        {dirty ? <span className="tw-menuweek-dirty">Cambios sin guardar</span> : <span className="tw-menuweek-clean">Guardado</span>}
                    </div>
                </div>

                <div className="tw-menuweek-topbar__right">
                    <button className="tw-menuweek-btn tw-menuweek-btn--ghost" type="button" onClick={startNewClick}>
                        <SvgIcon name="trash" />
                        Empezar nuevo
                    </button>

                    <button className="tw-menuweek-btn tw-menuweek-btn--ghost" type="button" onClick={revertClick}>
                        <SvgIcon name="undo" />
                        Revertir borrador
                    </button>

                    <button className="tw-menuweek-btn tw-menuweek-btn--ghost" type="button" onClick={saveDraftClick}>
                        <SvgIcon name="save" />
                        Guardar borrador
                    </button>

                    <button className="tw-menuweek-btn tw-menuweek-btn--primary" type="button" onClick={publishClick}>
                        <SvgIcon name="upload" />
                        Publicar
                    </button>
                </div>
            </div>

            <div className="tw-menuweek-steps">
                <StepPill n="1" title="Semana y días con venta" active />
                <div className="tw-menuweek-steps__divider" />
                <StepPill n="2" title="Armar opciones por día" active={!!canBuild} />
            </div>

            <div className="tw-menuweek-grid">
                <div className="tw-menuweek-card">
                    <div className="tw-menuweek-card__head">
                        <div className="tw-menuweek-card__title">1) Define la semana</div>
                        <div className="tw-menuweek-card__hint">Lunes a viernes</div>
                    </div>

                    <div className="tw-menuweek-card__body">
                        <div className="tw-menuweek-row tw-menuweek-row--2">
                            <div className="tw-menuweek-field">
                                <div className="tw-menuweek-label">Fecha inicio</div>
                                <input
                                    className="tw-menuweek-input"
                                    type="date"
                                    value={draft?.week?.start_date || ''}
                                    onChange={(e) => onChangeStartDate(e.target.value)}
                                />
                            </div>

                            <div className="tw-menuweek-field">
                                <div className="tw-menuweek-label">Fecha fin</div>
                                <input className="tw-menuweek-input" type="date" value={draft?.week?.end_date || ''} readOnly />
                            </div>
                        </div>

                        <div className="tw-menuweek-sep" />

                        <div className="tw-menuweek-field">
                            <div className="tw-menuweek-label">Días con venta</div>
                            <div className="tw-menuweek-days">
                                {DAY_META.map((d) => {
                                    const on = activeDayKeys.includes(d.key);
                                    const date = dayDateLabel[d.key] || '—';
                                    return (
                                        <button
                                            key={d.key}
                                            type="button"
                                            className={`tw-menuweek-daychip ${on ? 'is-on' : ''}`}
                                            onClick={() => toggleDay(d.key)}
                                        >
                                            <div className="tw-menuweek-daychip__top">
                                                <div className="tw-menuweek-daychip__name">{d.label}</div>
                                                <div className="tw-menuweek-daychip__dot" />
                                            </div>
                                            <div className="tw-menuweek-daychip__sub">{date}</div>
                                        </button>
                                    );
                                })}
                            </div>

                            {!activeDayKeys.length ? (
                                <div className="tw-menuweek-warn">Selecciona al menos un día con venta para poder armar el menú.</div>
                            ) : null}
                        </div>
                    </div>
                </div>

                <div className={`tw-menuweek-card ${canBuild ? '' : 'is-disabled'}`}>
                    <div className="tw-menuweek-card__head">
                        <div className="tw-menuweek-card__title">2) Arma el menú por día</div>
                        <div className="tw-menuweek-card__hint">
                            Selecciona un día → agrega opciones
                        </div>
                    </div>

                    <div className="tw-menuweek-card__body">
                        <div className="tw-menuweek-builder">
                            <div className="tw-menuweek-daylist">
                                {activeDayKeys.map((k) => {
                                    const meta = DAY_META.find((d) => d.key === k);
                                    const label = meta ? meta.label : k;
                                    const date = dayDateLabel[k] || '—';
                                    const c = dayCounters[k] || { lunch: 0, dinner: 0, total: 0 };
                                    const isActive = selectedDay === k;

                                    return (
                                        <button
                                            type="button"
                                            key={k}
                                            className={`tw-menuweek-dayrow ${isActive ? 'is-active' : ''}`}
                                            onClick={() => setSelectedDay(k)}
                                        >
                                            <div className="tw-menuweek-dayrow__main">
                                                <div className="tw-menuweek-dayrow__name">{label}</div>
                                                <div className="tw-menuweek-dayrow__date">{date}</div>
                                            </div>
                                            <div className="tw-menuweek-dayrow__stats">
                                                <div className="tw-menuweek-mini">
                                                    A: <span className="tw-menuweek-mini__n">{c.lunch}</span>
                                                </div>
                                                <div className="tw-menuweek-mini">
                                                    C: <span className="tw-menuweek-mini__n">{c.dinner}</span>
                                                </div>
                                                <div className="tw-menuweek-dayrow__chev">
                                                    <SvgIcon name="chev" />
                                                </div>
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>

                            <div className="tw-menuweek-daypanel">
                                <div className="tw-menuweek-daypanel__head">
                                    <div>
                                        <div className="tw-menuweek-daypanel__title">{selectedDayLabel}</div>
                                        <div className="tw-menuweek-daypanel__sub">
                                            Agrega una o más opciones. Cada opción es una combinación de plato fuerte + ensaladas/snacks.
                                        </div>
                                    </div>

                                    <button className="tw-menuweek-btn tw-menuweek-btn--primary" type="button" onClick={addOption}>
                                        <SvgIcon name="plus" />
                                        Agregar opción
                                    </button>
                                </div>

                                <div className="tw-menuweek-options">
                                    {dayOptions.length ? (
                                        dayOptions.map((o) => {
                                            const fuerte = o?.main_id ? fuerteMap.get(Number(o.main_id)) : null;
                                            const entries = Array.isArray(o?.entry_ids) ? o.entry_ids : [];
                                            const dinnerOnly = (o?.scope || 'both') === 'dinner';
                                            const snacks = dinnerOnly ? [] : (Array.isArray(o?.snack_ids) ? o.snack_ids : []);

                                            return (
                                                <OptionCard
                                                    key={o.id}
                                                    option={o}
                                                    fuerteName={fuerte?.nombre || ''}
                                                    entradasCount={entries.length}
                                                    snacksCount={snacks.length}
                                                    onEdit={() => openEditor(selectedDay, o.id)}
                                                    onRemove={() => removeOption(o.id)}
                                                />
                                            );
                                        })
                                    ) : (
                                        <div className="tw-menuweek-empty">
                                            <div className="tw-menuweek-empty__t">Aún no hay opciones para este día</div>
                                            <div className="tw-menuweek-empty__s">
                                                Presiona <strong>Agregar opción</strong> para empezar.
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div className="tw-menuweek-note">
                                    <strong>Regla:</strong> No puedes repetir el mismo plato fuerte dentro del mismo tipo (almuerzo/cena).
                                    Si una opción está en <strong>Ambas</strong>, cuenta para almuerzo y cena.
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Modal
                open={editing.open}
                title="Editar opción"
                onClose={() => closeEditor({ discard: true })}
                footer={
                    <div className="tw-menuweek-modal__actions">
                        <button className="tw-menuweek-btn tw-menuweek-btn--ghost" type="button" onClick={() => closeEditor({ discard: true })}>
                            Cancelar
                        </button>
                        <button
                            className="tw-menuweek-btn tw-menuweek-btn--primary"
                            type="button"
                            onClick={saveOption}
                            disabled={!!scopeConflict}
                            title={scopeConflict || ''}
                        >
                            Guardar opción
                        </button>
                    </div>
                }
            >
                {localOpt ? (
                    <>
                        <div className="tw-menuweek-row tw-menuweek-row--2">
                            <div className="tw-menuweek-field">
                                <div className="tw-menuweek-label">Aplica para</div>
                                <div className="tw-menuweek-seg">
                                    {SCOPE_META.map((s) => (
                                        <button
                                            key={s.value}
                                            type="button"
                                            className={`tw-menuweek-seg__btn ${(localOpt.scope || 'both') === s.value ? 'is-on' : ''}`}
                                            onClick={() => {
                                                if (s.value === 'dinner' && picker.kind === 'snack') closePicker();

                                                setLocalOpt((p) => {
                                                    const next = { ...p, scope: s.value };
                                                    if (s.value === 'dinner') next.snack_ids = [];
                                                    return next;
                                                });
                                            }}
                                        >
                                            {s.value === 'both' ? 'Ambas' : s.value === 'lunch' ? 'Almuerzo' : 'Cena'}
                                        </button>
                                    ))}
                                </div>
                                <div className="tw-menuweek-help">{scopeLabel(localOpt.scope || 'both')}</div>
                            </div>

                            <div className="tw-menuweek-field">
                                <div className="tw-menuweek-label">Plato fuerte</div>
                                <button className="tw-menuweek-pickbtn" type="button" onClick={() => openPicker('fuerte')}>
                                    <div className="tw-menuweek-pickbtn__main">
                                        <div className="tw-menuweek-pickbtn__t">Seleccionar</div>
                                        <div className="tw-menuweek-pickbtn__v">
                                            {localOpt.main_id ? (fuerteMap.get(Number(localOpt.main_id))?.nombre || '—') : '—'}
                                        </div>
                                    </div>
                                    <SvgIcon name="chev" />
                                </button>
                                {scopeConflict ? <div className="tw-menuweek-warn">{scopeConflict}</div> : null}
                            </div>
                        </div>

                        <div className="tw-menuweek-sep" />

                        <div className="tw-menuweek-row tw-menuweek-row--2">
                            <div className="tw-menuweek-field">
                                <div className="tw-menuweek-label">Ensaladas disponibles</div>
                                <button className="tw-menuweek-pickbtn" type="button" onClick={() => openPicker('entrada')}>
                                    <div className="tw-menuweek-pickbtn__main">
                                        <div className="tw-menuweek-pickbtn__t">Seleccionar ensaladas</div>
                                        <div className="tw-menuweek-pickbtn__v">
                                            {(Array.isArray(localOpt.entry_ids) ? localOpt.entry_ids.length : 0) || 0} seleccionadas
                                        </div>
                                    </div>
                                    <SvgIcon name="chev" />
                                </button>
                                <div className="tw-menuweek-chips">
                                    {(Array.isArray(localOpt.entry_ids) ? localOpt.entry_ids : []).slice(0, 6).map((id) => (
                                        <div key={id} className="tw-menuweek-chip">
                                            {entradaMap.get(Number(id))?.nombre || '—'}
                                        </div>
                                    ))}
                                    {(Array.isArray(localOpt.entry_ids) ? localOpt.entry_ids : []).length > 6 ? (
                                        <div className="tw-menuweek-chip tw-menuweek-chip--muted">+ más</div>
                                    ) : null}
                                </div>
                            </div>

                            <div className="tw-menuweek-field">
                                <div className="tw-menuweek-label">Snacks disponibles</div>

                                {isDinnerOnly ? (
                                    <div className="tw-menuweek-help">Snack no disponible para cena.</div>
                                ) : (
                                    <>
                                        <button className="tw-menuweek-pickbtn" type="button" onClick={() => openPicker('snack')}>
                                            <div className="tw-menuweek-pickbtn__main">
                                                <div className="tw-menuweek-pickbtn__t">Seleccionar snacks</div>
                                                <div className="tw-menuweek-pickbtn__v">
                                                    {(Array.isArray(localOpt.snack_ids) ? localOpt.snack_ids.length : 0) || 0} seleccionados
                                                </div>
                                            </div>
                                            <SvgIcon name="chev" />
                                        </button>

                                        <div className="tw-menuweek-chips">
                                            {(Array.isArray(localOpt.snack_ids) ? localOpt.snack_ids : []).slice(0, 6).map((id) => (
                                                <div key={id} className="tw-menuweek-chip">
                                                    {snackMap.get(Number(id))?.nombre || '—'}
                                                </div>
                                            ))}
                                            {(Array.isArray(localOpt.snack_ids) ? localOpt.snack_ids : []).length > 6 ? (
                                                <div className="tw-menuweek-chip tw-menuweek-chip--muted">+ más</div>
                                            ) : null}
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </>
                ) : null}
            </Modal>

            <PickerModal
                open={picker.open}
                title={
                    picker.kind === 'fuerte'
                        ? 'Selecciona el plato fuerte'
                        : picker.kind === 'entrada'
                            ? 'Selecciona ensaladas (pueden ser varias)'
                            : 'Selecciona snacks (pueden ser varios)'
                }
                items={pickerData.items}
                loading={pickerData.loading}
                hasMore={pickerData.hasMore}
                mode={picker.kind === 'fuerte' ? 'single' : 'multi'}
                valueSingle={picker.kind === 'fuerte' ? (localOpt?.main_id || null) : null}
                valueMulti={
                    picker.kind === 'entrada'
                        ? (Array.isArray(localOpt?.entry_ids) ? localOpt.entry_ids : [])
                        : picker.kind === 'snack'
                            ? (Array.isArray(localOpt?.snack_ids) ? localOpt.snack_ids : [])
                            : []
                }
                query={pickerData.q}
                onQueryChange={(text) => {
                    setPickerData((p) => ({ ...p, q: text }));
                }}
                onSearch={(q) => {
                    if (!picker.kind) return;
                    searchPicker({ kind: picker.kind, q, page: 1, append: false });
                }}
                onClose={closePicker}
                onConfirm={onPickerConfirm}
                onLoadMore={() => {
                    if (!picker.kind) return;
                    searchPicker({
                        kind: picker.kind,
                        q: pickerData.q,
                        page: (pickerData.page || 1) + 1,
                        append: true,
                    });
                }}
            />
        </div>
    );
}
